<template>
  <div class="itinerary-stops-step">
    <h3 class="itinerary-stops-step__title">Lista de paradas</h3>
    <p class="itinerary-stops-step__hint">
      Informe a quantidade de paradas terão neste roteiro.
    </p>

    <div class="itinerary-stops-step__list">
      <div
        v-for="(slot, index) in slots"
        :key="slot.key"
        class="itinerary-stops-step__card"
      >
        <div class="itinerary-stops-step__card-field">
          <span class="itinerary-stops-step__icon">
            <a-icon type="environment" />
          </span>

          <div v-if="slot.location" class="itinerary-stops-step__value">
            <span class="itinerary-stops-step__chip">
              <span class="itinerary-stops-step__chip-label">{{ slot.location.nome }}</span>
              <a-tooltip title="Trocar parada">
                <a-icon
                  type="close"
                  class="itinerary-stops-step__chip-close"
                  @click="clearSlot(slot)"
                />
              </a-tooltip>
            </span>
          </div>

          <a-select
            v-else
            show-search
            :value="undefined"
            :filter-option="false"
            :not-found-content="searching ? undefined : null"
            placeholder="Digite o CNPJ ou nome do fornecedor"
            class="itinerary-stops-step__select"
            @search="(value) => onSearch(slot, value)"
            @change="(id) => onSelect(slot, id)"
            @dropdownVisibleChange="(open) => onDropdown(slot, open)"
          >
            <a-spin v-if="searching" slot="notFoundContent" size="small" />
            <a-select-option v-for="loc in options" :key="loc.id" :value="loc.id">
              <div class="itinerary-stops-step__option">
                <strong>{{ loc.nome }}</strong>
                <span>{{ loc.cnpj }} - {{ loc.cidade }}, {{ loc.uf }}</span>
              </div>
            </a-select-option>
          </a-select>

          <a-tooltip v-if="index > 0" title="Remover parada">
            <button
              type="button"
              class="itinerary-stops-step__trash"
              @click="removeSlot(slot)"
            >
              <a-icon type="delete" />
            </button>
          </a-tooltip>
        </div>

        <div
          v-if="!slot.location && lookupError && activeSlotKey === slot.key"
          class="itinerary-stops-step__error"
        >
          {{ lookupError }}
        </div>

        <div v-if="slot.location" class="itinerary-stops-step__subtitle">
          <span class="itinerary-stops-step__subtitle-text">{{ slot.location.cnpj }}</span>
          <span class="itinerary-stops-step__dot" />
          <span class="itinerary-stops-step__subtitle-text">
            {{ slot.location.cidade }} - {{ slot.location.uf }}
          </span>
        </div>
      </div>
    </div>

    <a
      class="itinerary-stops-step__add"
      href="#"
      @click.prevent="addSlot"
    >
      <a-icon type="plus" /> Adicionar parada
    </a>
  </div>
</template>

<script>
import debounce from 'lodash/debounce';
import { getApiErrorMessage } from '@/modules/Itineraries/utils/handleApiError';

const MIN_SEARCH_LENGTH = 3;

let slotSeq = 0;
function nextKey() {
  slotSeq += 1;
  return `slot-${slotSeq}`;
}

export default {
  name: 'ItineraryStopsStep',
  props: {
    paradas: {
      type: Array,
      default: () => [],
    },
    itineraryId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      slots: [],
      options: [],
      searching: false,
      lookupError: '',
      activeSlotKey: null,
    };
  },
  computed: {
    filledLocations() {
      return this.slots.filter((s) => s.location).map((s) => s.location);
    },
    excludeIds() {
      return this.filledLocations.map((l) => l.id);
    },
  },
  created() {
    const initial = (this.paradas || []).map((loc) => ({
      key: nextKey(),
      location: { ...loc },
    }));
    if (!initial.length) {
      initial.push({ key: nextKey(), location: null });
    }
    this.slots = initial;
    this.debouncedSearch = debounce(this.fetchOptions, 300);
  },
  beforeDestroy() {
    this.debouncedSearch.cancel();
  },
  methods: {
    onDropdown(slot, open) {
      if (open) {
        this.activeSlotKey = slot.key;
        this.lookupError = '';
        this.fetchOptions('');
      }
    },
    onSearch(slot, value) {
      this.activeSlotKey = slot.key;
      this.lookupError = '';
      this.debouncedSearch(value);
    },
    async fetchOptions(query) {
      const term = String(query || '').trim();
      this.lookupError = '';
      if (term.length < MIN_SEARCH_LENGTH) {
        this.options = [];
        this.searching = false;
        return;
      }

      this.searching = true;
      try {
        const pessoas = await this.$store.dispatch('itineraries/searchPessoas', {
          itineraryId: this.itineraryId,
          search: term,
        });
        this.options = pessoas.filter((p) => !this.excludeIds.includes(p.id));
      } catch (error) {
        this.options = [];
        this.lookupError = getApiErrorMessage(
          error,
          'Não foi possível buscar o fornecedor'
        );
      } finally {
        this.searching = false;
      }
    },
    onSelect(slot, id) {
      if (!id) return;
      const location = this.options.find((loc) => loc.id === id);
      if (!location || this.excludeIds.includes(id)) return;
      slot.location = { ...location };
      this.options = [];
      this.lookupError = '';
      this.emitChange();
    },
    clearSlot(slot) {
      slot.location = null;
      this.emitChange();
    },
    removeSlot(slot) {
      const index = this.slots.findIndex((s) => s.key === slot.key);
      if (index <= 0) return;
      this.slots.splice(index, 1);
      this.emitChange();
    },
    addSlot() {
      this.slots.push({ key: nextKey(), location: null });
    },
    emitChange() {
      this.$emit('change', this.filledLocations.map((l) => ({ ...l })));
    },
  },
};
</script>

<style scoped lang="less">
@import '~@/shared/styles/variables.less';

.itinerary-stops-step {
  &__title {
    font-size: 16px;
    font-weight: 600;
    color: @color-text-dark;
    margin: 0 0 4px;
  }

  &__hint {
    font-size: 13px;
    color: @color-text-medium;
    margin: 0 0 20px;
  }

  &__list {
    max-height: 272px;
    overflow-y: auto;
    margin-bottom: 8px;
    padding-right: 4px;
  }

  &__card {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 12px 8px;
    margin-bottom: 8px;
    background: rgba(0, 0, 0, 0.02);
    border-radius: 8px;
  }

  &__card-field {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    min-height: 32px;
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: none;
    width: 32px;
    height: 32px;
    border-radius: 99px;
    background: rgba(0, 0, 0, 0.04);
    color: rgba(0, 0, 0, 0.45);
    font-size: 14px;
  }

  &__value {
    display: flex;
    align-items: center;
    flex: 1 1 auto;
    min-width: 0;
    height: 32px;
    padding: 0 4px;
    background: #fff;
    border: 1px solid #d9d9d9;
    border-radius: 6px;
  }

  &__chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    max-width: 100%;
    height: 24px;
    padding: 0 8px;
    background: rgba(0, 0, 0, 0.06);
    border-radius: 4px;
  }

  &__chip-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 14px;
    line-height: 22px;
    color: rgba(0, 0, 0, 0.88);
  }

  &__chip-close {
    flex: none;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.45);
    cursor: pointer;

    &:hover {
      color: rgba(0, 0, 0, 0.88);
    }
  }

  &__trash {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: none;
    width: 24px;
    height: 24px;
    padding: 0;
    border: none;
    background: transparent;
    border-radius: 4px;
    color: #d9363e;
    cursor: pointer;

    &:hover {
      background: rgba(217, 54, 62, 0.08);
    }
  }

  &__subtitle {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 4px;
    padding-left: 40px;
  }

  &__error {
    padding-left: 40px;
    font-size: 12px;
    line-height: 20px;
    color: @color-danger;
  }

  &__subtitle-text {
    font-size: 12px;
    line-height: 20px;
    color: rgba(0, 0, 0, 0.45);
  }

  &__dot {
    flex: none;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.15);
  }

  &__select {
    flex: 1;
    min-width: 0;
  }

  &__option {
    display: flex;
    flex-direction: column;
    line-height: 1.3;
    padding: 4px 0;

    span {
      font-size: 12px;
      color: @color-text-medium;
    }
  }

  &__add {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: @color-link;
    font-weight: 500;
    margin-top: 4px;

    &:hover {
      color: @color-link-hover;
    }
  }
}
</style>
